import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Typography,
    Button,
    Illustration,
    makeStyles,
    Card,
    CardContent,
    Tooltip
} from '@ellucian/react-design-system/core';
import {
    spacing60,
    spacing30,
    spacing50,
    spacing40,
} from '@ellucian/react-design-system/core/styles/tokens';
import { Icon } from '@ellucian/ds-icons/lib';
import ApplicationCard from './ApplicationCard';
import { useHistory, Link } from 'react-router-dom';
import { routes } from '../Helpers/Configuration';
import DeleteConfirmationDialog from '../Helpers/DeleteConfirmationDialog';
import { useApi } from '../ApiContext/';
import PageLoadIndicator from '../Helpers/PageLoadIndicator';
import { useDispatch } from 'react-redux';
import { Form as ReduxStateStore } from '@nsr/react-form';
import isLoadingReducer, { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice';
import orderBy from 'lodash/orderBy';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
const useStyles = makeStyles((theme) => ({
    root: {
        padding: `${spacing50} ${spacing40} ${spacing40} ${spacing40}`,
        width: '100%'
    },
    applicationList: {
        display: 'grid',
        gridGap: spacing50,
        gridTemplateRows: '1fr',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    content: {
        marginTop: spacing50,
        display: 'grid',
        gridGap: spacing50,
        gridTemplateColumns: '1fr',
    },
    profileContent:{
        maxWidth: '350px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    nameTag: {
        paddingBottom: spacing40
    },

    profileInfo: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: spacing40,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    icon: {
        marginRight: spacing30
    },
    button: {
        marginTop: spacing40,
        height: 'fit-content',
    },
    noApplications: {
        padding: spacing40,
        textAlign: 'center',
    },
    illustration: {
        marginBottom: spacing40,
    },
    [theme.breakpoints.up('sm')]: {
        root: {
            padding: spacing60,
            width: '100%',
        },
        header: {
            flexDirection: 'row'
        },
        content: {
            gridTemplateColumns: '2fr 1fr',
        },
        nameTag: {
            paddingBottom: spacing40
        },
        profileInfo: {},
        icon: {
            marginRight: spacing30
        },
    }
}));
const ApplicationHomeWrapper = props => {
    const additionalSlices = useMemo(() => ({
        isLoading: isLoadingReducer,
    }), []);
    return (
        <ReduxStateStore additionalSlices={additionalSlices}>
            <ApplicantHomeWrapper {...props} />
        </ReduxStateStore>
    );
};
ApplicationHomeWrapper.propTypes = {
};
const ApplicantHome = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [currentApplication, setCurrentApplication] = useState({});
    const [applications, setApplications] = useState([]);
    const [user] = useState(window.PRELOADED_STATE?.user ?? {});
    const {featureSupported} = window.PRELOADED_STATE ?? {};
    const history = useHistory();
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    // Create a ref for the main content region
    const mainRef = useRef(null);

    const accessApplication = async application => {
        history.push(routes.applicationFormStartWithId(application.id));
    };
    const refreshApplications = async () => {
        const applicationsFromApi = await api.applicationForms.getAll();
        if (featureSupported?.[SUPPORTED_FEATURES.applicationReview]) {
            if (applicationsFromApi?.length > 0) {
                const decisionsFromApi = await api.decisions.getStatus();
                if (decisionsFromApi?.length > 0) {
                    applicationsFromApi.map(application => {
                        const decision = decisionsFromApi.find(decision => decision?.applicationId === application?.id);
                        application.decisionStatus = decision?.decisionStatus ?? '';
                        application.decisionReason = decision?.decisionReason ?? '';
                        application.offerStatus = decision?.offerStatus ?? '';
                    });
                }
            }
        }
        setApplications(orderBy(applicationsFromApi, ['updatedAt'], ['desc']));
    };
   
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            dispatch(startLoading());
            await refreshApplications();
            setIsLoading(false);
            dispatch(stopLoading());
            if (mainRef.current) {
                mainRef.current.focus();
            }
        })();
    }, []);

    const nameValues = useMemo(() => {
        return {
            firstName: user.firstName,
            lastName: user.lastName,
        };
    }, [user]);

    const handleWithdraw = (applicationId) => {
        setCurrentApplication(applicationId);
        setShowWithdraw(true);
    };

    const withdrawApplication = async () => {
        setIsLoading(true);
        dispatch(startLoading());
        setShowWithdraw(false);
        await api.applicationForms.withdraw(currentApplication);
        await refreshApplications();
        setIsLoading(false);
        dispatch(stopLoading());
    };

    const combineCodeAndNumber = (value) => {
        if (!value) {
            return '';
        }
        else if (typeof value === 'string') {
            return value;
        }
        else {
            if(value.code && value.number) {
                return `+${value.code}${value.number}`;   
            }
            else if(value.number) {
                return value.number;
            }
            else { 
                return '';
            }            
        }
    };

    return (
        <div className={classes.root} ref={mainRef} tabIndex="-1" role="main">
            {isLoading && (
                // Wrap the loader in a container that is hidden from assistive technologies
                <div aria-hidden="true" style={{ pointerEvents: 'none' }}>
                    <PageLoadIndicator />
                </div>
            )}
            <DeleteConfirmationDialog
                isOpen={showWithdraw}
                onPrimaryClick={withdrawApplication}
                onSecondaryClick={() => setShowWithdraw(false)}
                title={formatMessage({ id: 'applicantHomeWithdrawConfirmationTitle' })}
                contentText={formatMessage({ id: 'applicantHomeWithdrawConfirmationMessage' })}
                primaryActionText={formatMessage({ id: 'applicationSubmissionYes' })}
                secondaryActionText={formatMessage({ id: 'applicationSubmissionNo' })}
            />
            <div className={classes.header}>
                <Typography variant="h1" className={classes.emailTag}>
                    <FormattedMessage id="applicationsHome" />
                </Typography>
                <Button className={classes.button} onClick={() => history.push(routes.newApplication)}>
                    <FormattedMessage id="applicantHomeNewApplication" />
                </Button>
            </div>
            <div className={classes.content}>
                <div 
                    className={classes.applicationList}
                    role='region'
                    aria-busy={isLoading ? 'true' : 'false'}
                >
                    {applications.length === 0 && !isLoading &&
                        <div className={classes.noApplications}>
                            <div className={classes.illustration}>
                                <Illustration name="no-tasks" />
                            </div>
                            <Typography variant="h2">
                                <FormattedMessage id="applicantHomeNoApplicationsDetail" />
                            </Typography>
                            <br />
                            <Typography variant="body1">
                                <Link to={routes.newApplication}>
                                    <FormattedMessage id="applicantHomeStartApplication" />
                                </Link>
                            </Typography>
                        </div>
                    }
                    {applications.map(it => {
                        return (
                            <div key={it.id}>
                                <ApplicationCard
                                    application={it}
                                    accessApplication={accessApplication}
                                    withdrawApplication={handleWithdraw}
                                />
                            </div>
                        );
                    })}
                </div>
                <div>
                    <Card accent="secondary">
                        <CardContent>
                            <Tooltip title = {nameValues.firstName + ' '+ nameValues.lastName}>
                                <Typography variant="h2" className={classes.profileContent}>
                                    <FormattedMessage id="applicantHomeApplicantName" values={nameValues} />
                                </Typography>
                            </Tooltip>
                            <Tooltip title = {user.email} className={classes.profileInfo}>
                                <Typography>
                                    <Icon name="email" className={classes.icon}/> 
                                    <div className={classes.profileContent}>{user.email}</div>
                                </Typography>  
                            </Tooltip>
                            <Tooltip title = {combineCodeAndNumber(user.phone)}>
                                <Typography className={classes.profileInfo}>
                                    <Icon name="phone" className={classes.icon} />
                                    <div className={classes.profileContent}>{combineCodeAndNumber(user.phone)}</div>
                                </Typography>
                            </Tooltip>  
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

const ApplicantHomeWrapper = ApplicantHome;

export default ApplicationHomeWrapper;
